// 移植元: poc_test_designer/src/lib/bff-sync-hydrate-spec.ts
// spec text の出力を poc_test_designer と一致させるため、ロジックは原本と等価に保つこと。
import type { UseCase } from './types';
import { resolveItemCategoryEntries } from './item-category';

export type TargetDisplayNameResolver = (targetId: string) => string | null;

export interface TargetNameLookupRow {
  id: number;
  name: string;
  externalId?: string | null;
}

interface HydrateSpecInput {
  targetType?: string | null;
  name: string;
  description?: string | null;
  params: unknown;
  resolveTargetDisplayName?: TargetDisplayNameResolver;
}

/** transitions.targetId（DB id / externalId）を表示名へ解決する */
export function build_target_display_name_resolver(
  targets: TargetNameLookupRow[],
): TargetDisplayNameResolver {
  const map = new Map<string, string>();
  for (const target of targets) {
    map.set(String(target.id), target.name);
    const external_id = get_non_empty_string(target.externalId);
    if (external_id) {
      map.set(external_id, target.name);
    }
  }
  return (targetId: string) => map.get(targetId) ?? null;
}

export interface HydratedSpecText {
  fullSpec: string | null;
  specText: string | null;
}

type UnknownRecord = Record<string, unknown>;
type DisplayTextConditionEntry = { text: string | null; conditions: string[] };

const IO_VALUES = new Set(['input', 'output', 'both', 'system']);
const HTML_ATTRS_EXCLUDED_FROM_HTML_SECTION = new Set(['defaultValue', 'default_value', 'checkboxDefaultValues', 'checkbox_default_values']);

function is_record(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null;
}

function sanitize_text_value(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return null;
  }
  if (trimmed === '-') {
    return null;
  }
  const without_list_prefix = trimmed.replace(/^- /, '').trim();
  if (without_list_prefix.length === 0 || without_list_prefix === '-') {
    return null;
  }
  return without_list_prefix;
}

function get_non_empty_string(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  return sanitize_text_value(value);
}

function get_value_by_aliases(record: UnknownRecord, aliases: string[]): unknown {
  for (const alias of aliases) {
    if (alias in record) {
      return record[alias];
    }
  }
  return undefined;
}

function get_non_empty_string_from_aliases(record: UnknownRecord, aliases: string[]): string | null {
  return get_non_empty_string(get_value_by_aliases(record, aliases));
}

function get_string_array(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const result: string[] = [];
  for (const item of value) {
    const text = get_non_empty_string(item);
    if (text) {
      result.push(text);
    }
  }
  return result;
}

function get_string_array_from_aliases(record: UnknownRecord, aliases: string[]): string[] {
  return get_string_array(get_value_by_aliases(record, aliases));
}

function get_boolean_array(value: unknown): boolean[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((item): item is boolean => typeof item === 'boolean');
}

function get_transition_records(value: unknown): UnknownRecord[] {
  if (Array.isArray(value)) {
    const result: UnknownRecord[] = [];
    for (const item of value) {
      if (is_record(item)) {
        result.push(item);
      }
    }
    return result;
  }
  if (is_record(value)) {
    return [value];
  }
  return [];
}

function get_display_text_entries(params: UnknownRecord): DisplayTextConditionEntry[] {
  const entries: DisplayTextConditionEntry[] = [];
  const display_text_entries = get_value_by_aliases(params, ['display_text_entries']);
  if (Array.isArray(display_text_entries)) {
    for (const entry of display_text_entries) {
      if (!is_record(entry)) {
        continue;
      }
      entries.push({
        text: get_non_empty_string(entry.text),
        conditions: get_string_array(entry.conditions),
      });
    }
  }
  if (entries.length > 0) {
    return entries;
  }

  const fallback_text = get_non_empty_string_from_aliases(params, ['text']);
  const fallback_conditions = get_string_array_from_aliases(params, ['conditions']);
  if (fallback_text || fallback_conditions.length > 0) {
    return [{ text: fallback_text, conditions: fallback_conditions }];
  }

  return [];
}

function get_component_condition_entries(params: UnknownRecord): Array<{ componentId: string; conditions: string[] }> {
  const raw = get_value_by_aliases(params, ['componentConditions', 'component_conditions']);
  if (!Array.isArray(raw)) {
    return [];
  }

  const entries: Array<{ componentId: string; conditions: string[] }> = [];
  for (const item of raw) {
    if (!is_record(item)) {
      continue;
    }
    const component_id = get_non_empty_string_from_aliases(item, ['componentId', 'component_id']);
    const conditions = get_string_array(item.conditions);
    if (!component_id || conditions.length === 0) {
      continue;
    }
    entries.push({ componentId: component_id, conditions });
  }
  return entries;
}

function get_db_mapping_rows(params: UnknownRecord): Array<{ table: string; column: string }> {
  const raw = get_value_by_aliases(params, ['dbMapping', 'db_mapping']);
  if (!Array.isArray(raw)) {
    return [];
  }

  const rows: Array<{ table: string; column: string }> = [];
  for (const item of raw) {
    if (!is_record(item)) {
      continue;
    }
    const table = get_non_empty_string(item.table);
    const column = get_non_empty_string(item.column);
    if (table && column) {
      rows.push({ table, column });
    }
  }
  return rows;
}

function get_usecases(value: unknown): UseCase[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const usecases: UseCase[] = [];
  for (const item of value) {
    if (!is_record(item)) {
      continue;
    }
    const usecase_id = get_non_empty_string(item.usecase_id);
    const name = get_non_empty_string(item.name);
    const title = get_non_empty_string(item.title);
    if (!usecase_id || !name || !title) {
      continue;
    }
    usecases.push({
      usecase_id,
      name,
      title,
      description: get_non_empty_string(item.description) ?? '',
      steps: [],
      policies: [],
      parameters: [],
      status: undefined,
      parent_usecase_id: get_non_empty_string(item.parent_usecase_id) ?? undefined,
      type: get_non_empty_string(item.type) ?? undefined,
      preparation_usecase_id: get_non_empty_string(item.preparation_usecase_id),
    });
  }
  return usecases;
}

function create_header(name: string, description?: string | null): string[] {
  const lines = [`# ${name}`];
  if (description && description.trim().length > 0) {
    lines.push('', description.trim());
  }
  return lines;
}

function push_section(lines: string[], title: string): void {
  lines.push('', `## ${title}`);
}

function push_string_line(lines: string[], label: string, value: string | null): void {
  if (!value) {
    return;
  }
  lines.push(`- ${label}: ${value}`);
}

function finalize_spec_text(lines: string[]): HydratedSpecText {
  const spec_text = lines.join('\n').trim();
  return { fullSpec: null, specText: spec_text.length > 0 ? spec_text : null };
}

function hydrate_service(
  name: string,
  description: string | null | undefined,
  params: UnknownRecord,
): HydratedSpecText {
  const lines = create_header(name, description);
  const base_url = get_value_by_aliases(params, ['baseUrl', 'base_url']);
  if (typeof base_url === 'string' && base_url.trim().length > 0) {
    lines.push('', '## Base URL', `- ${base_url.trim()}`);
  } else if (Array.isArray(base_url)) {
    const url_lines: string[] = [];
    for (const base_url_entry of base_url) {
      if (typeof base_url_entry === 'string') {
        const trimmed = base_url_entry.trim();
        if (trimmed.length > 0) {
          url_lines.push(`- ${trimmed}`);
        }
        continue;
      }
      if (!is_record(base_url_entry)) {
        continue;
      }
      const name_value = get_non_empty_string_from_aliases(base_url_entry, ['name', 'env', 'environment']);
      const url_value = get_non_empty_string_from_aliases(base_url_entry, ['url', 'base_url', 'baseUrl', 'value']);
      if (name_value && url_value) {
        url_lines.push(`- ${name_value}: ${url_value}`);
        continue;
      }
      const keys = Object.keys(base_url_entry).sort((a, b) => a.localeCompare(b));
      for (const key of keys) {
        const value = get_non_empty_string(base_url_entry[key]);
        if (value) {
          url_lines.push(`- ${key}: ${value}`);
        }
      }
    }
    if (url_lines.length > 0) {
      lines.push('', '## Base URL');
      for (const url_line of url_lines) {
        lines.push(url_line);
      }
    }
  } else if (is_record(base_url)) {
    const keys = Object.keys(base_url).sort((a, b) => a.localeCompare(b));
    if (keys.length > 0) {
      lines.push('', '## Base URL');
      for (const key of keys) {
        const value = get_non_empty_string(base_url[key]);
        if (value) {
          lines.push(`- ${key}: ${value}`);
        }
      }
    }
  }

  const markdown = lines.join('\n').trim();
  if (!markdown) {
    return { fullSpec: null, specText: null };
  }
  return { fullSpec: markdown, specText: markdown };
}

function push_component_conditions(lines: string[], params: UnknownRecord): void {
  const entries = get_component_condition_entries(params);
  if (entries.length === 0) {
    return;
  }

  push_section(lines, '共通コンポーネント表示条件');
  for (const entry of entries) {
    lines.push('', `### ${entry.componentId}`);
    for (const condition of entry.conditions) {
      lines.push(`- ${condition}`);
    }
  }
}

function hydrate_view(
  name: string,
  description: string | null | undefined,
  params: UnknownRecord,
): HydratedSpecText {
  const lines = create_header(name, description);
  const paths = Array.isArray(params.paths) ? params.paths : [];
  const path_rows: string[] = [];
  for (const path_value of paths) {
    if (!is_record(path_value)) {
      continue;
    }
    const path_text = get_non_empty_string(path_value.path);
    if (path_text) {
      path_rows.push(path_text);
    }
  }
  if (path_rows.length > 0) {
    lines.push('', '## Paths');
    for (const path_text of path_rows) {
      lines.push(`- ${path_text}`);
    }
  }
  const fallback_path = get_non_empty_string(get_value_by_aliases(params, ['path']));
  if (path_rows.length === 0 && fallback_path) {
    lines.push('', '## Paths', `- ${fallback_path}`);
  }

  if (params.entry_point === true) {
    lines.push('', '## Entry Point', '- この画面はエントリーポイントです');
  }

  const component_ids = get_string_array(params.component_ids);
  if (component_ids.length > 0) {
    lines.push('', '## Components');
    for (const component_id of component_ids) {
      lines.push(`- ${component_id}`);
    }
  }

  const conditions = get_string_array_from_aliases(params, ['conditions']);
  push_conditions_block(lines, '表示条件', conditions);

  push_component_conditions(lines, params);

  // #884: usecase → feature/procedure 統合の一環で、将来この節は procedure 節へ移行予定。
  // 現段階では poc_test_designer との spec text 原本パリティを維持するため、出力構造・見出しは変更しない（安全側）。
  const usecases = get_usecases(params.usecases);
  if (usecases.length > 0) {
    lines.push('', '## ユースケース（SSTF同期）');
    for (const uc of usecases) {
      lines.push('', `### ${uc.title}`);
      lines.push(`- Name: ${uc.name}`);
      if (uc.description.trim().length > 0) {
        lines.push(`- Description: ${uc.description.trim()}`);
      }
    }
  }

  const full_spec = lines.join('\n').trim();
  if (full_spec.length === 0) {
    return { fullSpec: null, specText: null };
  }
  return { fullSpec: full_spec, specText: full_spec };
}

function push_conditions_block(lines: string[], title: string, conditions: string[]): void {
  if (conditions.length === 0) {
    return;
  }
  push_section(lines, title);
  for (const condition of conditions) {
    lines.push(`- ${condition}`);
  }
}

function push_ui_block_characteristics(lines: string[], flags: string[]): void {
  if (flags.length === 0) {
    return;
  }
  push_section(lines, 'UI ブロック特性');
  for (const flag of flags) {
    lines.push(`- ${flag}`);
  }
}

function get_section_ui_flags(params: UnknownRecord): string[] {
  const flags: string[] = [];
  if (params.isIframe === true) {
    flags.push('iframe');
  }
  if (params.isShadow === true) {
    flags.push('Shadow DOM');
  }
  if (params.isTemplate === true) {
    flags.push('テンプレート（繰り返し要素）');
  }
  return flags;
}

function get_overlay_ui_flags(params: UnknownRecord): string[] {
  const flags: string[] = [];
  if (params.isModal === true) {
    flags.push('モーダル');
  }
  if (params.isTemplate === true) {
    flags.push('テンプレート（繰り返し要素）');
  }
  return flags;
}

function push_item_display_spec(lines: string[], entries: DisplayTextConditionEntry[]): void {
  if (entries.length === 0) {
    return;
  }

  const visibility_only = entries.filter((entry) => !entry.text && entry.conditions.length > 0);
  const content_only = entries.filter((entry) => entry.text && entry.conditions.length === 0);
  const combined = entries.filter((entry) => entry.text && entry.conditions.length > 0);

  if (visibility_only.length > 0) {
    push_section(lines, '表示条件');
    if (visibility_only.length === 1) {
      for (const condition of visibility_only[0].conditions) {
        lines.push(`- ${condition}`);
      }
    } else {
      for (let index = 0; index < visibility_only.length; index++) {
        const entry = visibility_only[index];
        lines.push(`### 条件セット ${index + 1}`);
        for (const condition of entry.conditions) {
          lines.push(`- ${condition}`);
        }
      }
    }
  }

  if (content_only.length > 0) {
    push_section(lines, '表示内容');
    for (const entry of content_only) {
      if (entry.text) {
        lines.push(`- ${entry.text}`);
      }
    }
  }

  if (combined.length > 0) {
    push_section(lines, '表示パターン');
    for (let index = 0; index < combined.length; index++) {
      const entry = combined[index];
      lines.push(`### パターン ${index + 1}`);
      lines.push('- この項目の表示条件:');
      for (const condition of entry.conditions) {
        lines.push(`  - ${condition}`);
      }
      if (entry.text) {
        lines.push(`- 表示文言: ${entry.text}`);
      }
    }
  }
}

function push_item_info(lines: string[], params: UnknownRecord): void {
  const item_category = get_non_empty_string_from_aliases(params, ['itemCategory', 'item_category']);
  const item_sub_category = get_non_empty_string_from_aliases(params, ['itemSubCategory', 'item_sub_category']);
  const legacy_type = get_non_empty_string_from_aliases(params, ['type']);
  const item_categories = resolveItemCategoryEntries(params);
  const has_explicit_item_categories =
    Array.isArray(params.itemCategories) || Array.isArray(params.item_categories);
  const object_types = get_string_array_from_aliases(params, ['objectTypes', 'object_types']);

  const info_lines: string[] = [];
  if (has_explicit_item_categories && item_categories.length > 0) {
    info_lines.push('- itemCategories:');
    for (const entry of item_categories) {
      const sub = entry.itemSubCategory ? ` / ${entry.itemSubCategory}` : '';
      const objectType = entry.objectType ? ` (objectType: ${entry.objectType})` : '';
      info_lines.push(`  - ${entry.itemCategory}${sub}${objectType}`);
    }
  } else {
    push_string_line(info_lines, 'itemCategory', item_category);
    push_string_line(info_lines, 'itemSubCategory', item_sub_category);
  }
  if (object_types.length > 0) {
    info_lines.push(`- objectTypes: ${object_types.join(', ')}`);
  }
  if (!item_category && legacy_type) {
    push_string_line(info_lines, 'type', legacy_type);
  }
  if (info_lines.length > 0) {
    push_section(lines, '項目情報');
    lines.push(...info_lines);
  }
}

function get_item_is_template_label(params: UnknownRecord): string | null {
  if (!('isTemplate' in params) && !('is_template' in params)) {
    return null;
  }
  const value = get_value_by_aliases(params, ['isTemplate', 'is_template']);
  if (value === true) {
    return 'はい';
  }
  if (value === false) {
    return 'いいえ';
  }
  return '不明';
}

function push_spec_integration(lines: string[], params: UnknownRecord): void {
  const form_category = get_non_empty_string_from_aliases(params, ['formCategory', 'form_category']);
  const io = get_non_empty_string(get_value_by_aliases(params, ['io']));
  const db_rows = get_db_mapping_rows(params);
  const is_template_label = get_item_is_template_label(params);

  if (!form_category && (!io || !IO_VALUES.has(io)) && db_rows.length === 0 && !is_template_label) {
    return;
  }

  push_section(lines, '仕様連携');
  push_string_line(lines, 'formCategory', form_category);
  if (io && IO_VALUES.has(io)) {
    lines.push(`- io: ${io}`);
  }
  if (is_template_label) {
    lines.push(`- isTemplate: ${is_template_label}`);
  }
  if (db_rows.length > 0) {
    lines.push('- dbMapping:');
    for (const row of db_rows) {
      lines.push(`  - ${row.table}.${row.column}`);
    }
  }
}

function push_initial_values(lines: string[], params: UnknownRecord): void {
  if (!is_record(params.htmlAttributes)) {
    return;
  }

  const default_value = get_non_empty_string(
    get_value_by_aliases(params.htmlAttributes, ['defaultValue', 'default_value']),
  );
  const checkbox_defaults = get_boolean_array(
    get_value_by_aliases(params.htmlAttributes, ['checkboxDefaultValues', 'checkbox_default_values']),
  );

  if (!default_value && checkbox_defaults.length === 0) {
    return;
  }

  push_section(lines, '初期値');
  if (default_value) {
    lines.push(`- defaultValue: ${default_value}`);
  }
  if (checkbox_defaults.length > 0) {
    lines.push('- checkboxDefaultValues:');
    for (const checked of checkbox_defaults) {
      lines.push(`  - ${checked ? 'あり' : 'なし'}`);
    }
  }
}

function push_available_choices(lines: string[], params: UnknownRecord): void {
  const available_choices = get_value_by_aliases(params, ['available_choices']);
  if (!Array.isArray(available_choices) || available_choices.length === 0) {
    return;
  }

  const checkbox_defaults = is_record(params.htmlAttributes)
    ? get_boolean_array(
        get_value_by_aliases(params.htmlAttributes, ['checkboxDefaultValues', 'checkbox_default_values']),
      )
    : [];

  push_section(lines, '選択肢');
  for (let index = 0; index < available_choices.length; index++) {
    const choice = available_choices[index];
    if (typeof choice === 'string') {
      const choice_text = choice.trim();
      if (choice_text.length > 0) {
        lines.push(`- ${choice_text}`);
      }
      continue;
    }
    if (!is_record(choice)) {
      continue;
    }
    const value = get_non_empty_string(choice.value);
    const label = get_non_empty_string(choice.label);
    const description_text = get_non_empty_string(choice.description);
    const suffix_parts: string[] = [];
    if (choice.checked === true) {
      suffix_parts.push('[初期選択]');
    }
    if (index < checkbox_defaults.length) {
      suffix_parts.push(`[初期チェック: ${checkbox_defaults[index] ? 'あり' : 'なし'}]`);
    }
    const suffix = suffix_parts.length > 0 ? ` ${suffix_parts.join(' ')}` : '';
    if (value || label) {
      const base = value && label ? `${label} (${value})` : (label ?? value ?? '');
      lines.push(`- ${base}${description_text ? `: ${description_text}` : ''}${suffix}`);
    }
  }
}

function push_html_attributes(lines: string[], params: UnknownRecord): void {
  if (!is_record(params.htmlAttributes)) {
    return;
  }

  const keys = Object.keys(params.htmlAttributes)
    .filter((key) => !HTML_ATTRS_EXCLUDED_FROM_HTML_SECTION.has(key))
    .sort((a, b) => a.localeCompare(b));
  if (keys.length === 0) {
    return;
  }

  const attr_lines: string[] = [];
  for (const key of keys) {
    const value = params.htmlAttributes[key];
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      attr_lines.push(`- ${key}: ${String(value)}`);
    }
    if (Array.isArray(value)) {
      attr_lines.push(`- ${key}:`);
      for (const item of value) {
        if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') {
          attr_lines.push(`  - ${String(item)}`);
        }
      }
    }
  }

  if (attr_lines.length === 0) {
    return;
  }

  push_section(lines, 'HTML Attributes');
  lines.push(...attr_lines);
}

function push_record_primitives(lines: string[], title: string, value: unknown): void {
  if (!is_record(value)) {
    return;
  }
  const keys = Object.keys(value).sort((a, b) => a.localeCompare(b));
  if (keys.length === 0) {
    return;
  }
  const primitive_lines: string[] = [];
  for (const key of keys) {
    const item = value[key];
    if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') {
      primitive_lines.push(`- ${key}: ${String(item)}`);
    }
  }
  if (primitive_lines.length === 0) {
    return;
  }
  push_section(lines, title);
  for (const primitive_line of primitive_lines) {
    lines.push(primitive_line);
  }
}

function push_internal_process_flow(lines: string[], value: unknown): void {
  if (!Array.isArray(value) || value.length === 0) {
    return;
  }

  const flow_entries = value.filter((entry): entry is UnknownRecord => is_record(entry));
  if (flow_entries.length === 0) {
    return;
  }

  push_section(lines, '内部処理フロー');
  let step_index = 0;
  for (const entry of flow_entries) {
    const kind = get_non_empty_string_from_aliases(entry, ['kind']);

    if (kind === 'parallel_start') {
      lines.push('### 並列処理開始');
      continue;
    }
    if (kind === 'parallel_end') {
      lines.push('### 並列処理終了');
      continue;
    }

    step_index += 1;
    const step_title = get_non_empty_string_from_aliases(entry, ['processName', 'process_name']) ?? `ステップ ${step_index}`;
    lines.push(`### ${step_title}`);

    if (kind && kind !== 'step') {
      lines.push(`- 種別: ${kind}`);
    } else if (kind === 'step') {
      lines.push('- 種別: step');
    }

    const is_async = entry.isAsync;
    if (typeof is_async === 'boolean') {
      lines.push(`- 非同期: ${is_async ? 'true' : 'false'}`);
    }
    const precondition = get_non_empty_string_from_aliases(entry, ['precondition']);
    if (precondition) {
      lines.push(`- 前提条件: ${precondition}`);
    }
    const process_content = get_non_empty_string_from_aliases(entry, ['processContent', 'process_content']);
    if (process_content) {
      lines.push(`- 処理内容: ${process_content}`);
    }
    const expected_success = get_non_empty_string_from_aliases(entry, ['expectedChangeOnSuccess', 'expected_change_on_success']);
    if (expected_success) {
      lines.push(`- 成功時の変化: ${expected_success}`);
    }
    const expected_error = get_non_empty_string_from_aliases(entry, ['expectedChangeOnError', 'expected_change_on_error']);
    if (expected_error) {
      lines.push(`- エラー時の変化: ${expected_error}`);
    }
  }
}

const TRANSITION_TYPE_SPEC_LABELS: Record<string, { label: string; description: string }> = {
  same_tab: {
    label: '通常遷移',
    description: '現在開いているタブで遷移を行います',
  },
  new_tab: {
    label: '別タブ',
    description: '新しいタブを開いて遷移を行います',
  },
  new_window: {
    label: '別ウィンドウ',
    description: '新しいウィンドウを開いて遷移を行います',
  },
};

function format_transition_type_for_spec(transitionType: string): string {
  const mapped = TRANSITION_TYPE_SPEC_LABELS[transitionType];
  if (mapped) {
    return `${mapped.label}（${mapped.description}）`;
  }
  return transitionType;
}

function resolve_transition_target_label(
  targetId: string | null,
  resolveTargetDisplayName?: TargetDisplayNameResolver,
): string {
  if (!targetId) {
    return 'unknown';
  }
  return resolveTargetDisplayName?.(targetId) ?? targetId;
}

function push_transitions(
  lines: string[],
  params: UnknownRecord,
  resolveTargetDisplayName?: TargetDisplayNameResolver,
): void {
  const transitions = get_transition_records(get_value_by_aliases(params, ['transitions']));
  if (transitions.length === 0) {
    return;
  }

  lines.push('', '## Transitions');
  for (const transition_value of transitions) {
    const target = resolve_transition_target_label(
      get_non_empty_string(transition_value.targetId),
      resolveTargetDisplayName,
    );
    const condition = get_non_empty_string(transition_value.condition);
    const description_text = get_non_empty_string(transition_value.description);
    const transition_type = get_non_empty_string_from_aliases(transition_value, ['transitionType', 'transition_type']);
    const details: string[] = [];
    if (condition) {
      details.push(`condition: ${condition}`);
    }
    if (description_text) {
      details.push(`description: ${description_text}`);
    }
    if (transition_type) {
      details.push(`遷移種別: ${format_transition_type_for_spec(transition_type)}`);
    }
    lines.push(`- target: ${target}${details.length > 0 ? ` (${details.join(', ')})` : ''}`);
  }
}

function push_common_ui_tail(
  lines: string[],
  params: UnknownRecord,
  resolveTargetDisplayName?: TargetDisplayNameResolver,
): void {
  push_transitions(lines, params, resolveTargetDisplayName);
  push_record_primitives(lines, 'ARIA Attributes', get_value_by_aliases(params, ['ariaAttributes', 'aria_attributes']));
  push_record_primitives(lines, 'State', get_value_by_aliases(params, ['state']));
  push_record_primitives(lines, 'Options', get_value_by_aliases(params, ['options']));
  push_internal_process_flow(lines, get_value_by_aliases(params, ['internalProcessFlow', 'internal_process_flow']));
}

function hydrate_item(
  name: string,
  description: string | null | undefined,
  params: UnknownRecord,
  resolveTargetDisplayName?: TargetDisplayNameResolver,
): HydratedSpecText {
  const lines = create_header(name, description);

  push_item_info(lines, params);

  const display_entries = get_display_text_entries(params);
  push_item_display_spec(lines, display_entries);

  push_spec_integration(lines, params);
  push_initial_values(lines, params);
  push_available_choices(lines, params);
  push_html_attributes(lines, params);
  push_common_ui_tail(lines, params, resolveTargetDisplayName);

  return finalize_spec_text(lines);
}

function hydrate_section(
  name: string,
  description: string | null | undefined,
  params: UnknownRecord,
  resolveTargetDisplayName?: TargetDisplayNameResolver,
): HydratedSpecText {
  const lines = create_header(name, description);

  const text = get_non_empty_string_from_aliases(params, ['text']);
  if (text) {
    push_section(lines, '項目情報');
    push_string_line(lines, 'text', text);
  }

  const conditions = get_string_array_from_aliases(params, ['conditions']);
  push_conditions_block(lines, '表示条件', conditions);

  push_ui_block_characteristics(lines, get_section_ui_flags(params));
  push_common_ui_tail(lines, params, resolveTargetDisplayName);

  return finalize_spec_text(lines);
}

function hydrate_overlay(
  name: string,
  description: string | null | undefined,
  params: UnknownRecord,
  resolveTargetDisplayName?: TargetDisplayNameResolver,
): HydratedSpecText {
  const lines = create_header(name, description);

  const text = get_non_empty_string_from_aliases(params, ['text']);
  if (text) {
    push_section(lines, '項目情報');
    push_string_line(lines, 'text', text);
  }

  const conditions = get_string_array_from_aliases(params, ['conditions']);
  push_conditions_block(lines, '表示条件', conditions);

  push_ui_block_characteristics(lines, get_overlay_ui_flags(params));
  push_common_ui_tail(lines, params, resolveTargetDisplayName);

  return finalize_spec_text(lines);
}

function hydrate_component(name: string, description: string | null | undefined): HydratedSpecText {
  const lines = create_header(name, description);
  const spec_text = lines.join('\n').trim();
  return { fullSpec: null, specText: spec_text.length > 0 ? spec_text : null };
}

export function hydrate_spec_text_from_params({
  targetType,
  name,
  description,
  params,
  resolveTargetDisplayName,
}: HydrateSpecInput): HydratedSpecText {
  const normalized_type = targetType?.toLowerCase();
  if (!is_record(params)) {
    if (normalized_type === 'component') {
      return hydrate_component(name, description);
    }
    return { fullSpec: null, specText: null };
  }

  switch (normalized_type) {
    case 'service':
      return hydrate_service(name, description, params);
    case 'view':
      return hydrate_view(name, description, params);
    case 'item':
      return hydrate_item(name, description, params, resolveTargetDisplayName);
    case 'section':
      return hydrate_section(name, description, params, resolveTargetDisplayName);
    case 'overlay':
      return hydrate_overlay(name, description, params, resolveTargetDisplayName);
    case 'component':
      return hydrate_component(name, description);
    default:
      return { fullSpec: null, specText: null };
  }
}
